import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { PUBLIC_URL, AUTH_URL, GoogleOAuthClientID, PRODUCT_ID } from "./../../const";
import { useAuth } from "./../../AuthContext";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import LanguageSwitcher from "../core/switch-language";

const translations = {
    en: {
        title: "Register New Account",
        at: "at My-Sites-Guard.com",
        clientArea: "Client Area",
        orSignUpWith: "or sign up with",
        email: "Email",
        password: "Password",
        show: "Show",
        hide: "Hide",
        next: "Next",
        loading: "Loading...",
        backToLogin: "Back to Login",
        step2Title: "Personal Information",
        firstName: "First Name",
        lastName: "Last Name",
        companyName: "Company Name",
        step3Title: "Pricing Plan",
        choosePlan: "Choose Tariff Plan",
        selectPlan: "Select a plan",
        perMonth: "/ month",
        step4Title: "Complete the Registration",
        codeSentNote: "A verification code has been sent to your email. Please enter it below.",
        sentTo: "Sent to Email:",
        type6digit: "Type your 6-digit security code",
        completeRegistration: "Complete Registration",
        accountExists: <>An account with this email already exists. <a href="/sign-in" className="fw-bold text-danger" style={{textDecoration: "underline"}}>Please Log In</a>.</>,
        requiredEmailPass: "Both email and password are required",
        requiredAllFields: "All fields are required",
        pleaseSelectPlan: "Please select a tariff plan",
        registerFailed: "Registration failed. Please try again.",
        invalidCode: "Invalid or expired code. Please try again.",
        pricingLoadFail: "Failed to load pricing plans",
    },
    ru: {
        title: "Регистрация нового аккаунта",
        at: "на My-Sites-Guard.com",
        clientArea: "Личный кабинет",
        orSignUpWith: "или зарегистрируйтесь через",
        email: "Email",
        password: "Пароль",
        show: "Показать",
        hide: "Скрыть",
        next: "Далее",
        loading: "Загрузка...",
        backToLogin: "Назад ко входу",
        step2Title: "Личные данные",
        firstName: "Имя",
        lastName: "Фамилия",
        companyName: "Компания",
        step3Title: "Тарифный план",
        choosePlan: "Выберите тариф",
        selectPlan: "Выберите план",
        perMonth: "/ месяц",
        step4Title: "Завершение регистрации",
        codeSentNote: "На ваш email отправлен код подтверждения. Введите его ниже.",
        sentTo: "Отправлено на Email:",
        type6digit: "Введите 6-значный код",
        completeRegistration: "Завершить регистрацию",
        accountExists: <>Аккаунт с таким email уже существует. <a href="/sign-in" className="fw-bold text-danger" style={{textDecoration: "underline"}}>Войти</a>.</>,
        requiredEmailPass: "Нужны и email, и пароль",
        requiredAllFields: "Заполните все поля",
        pleaseSelectPlan: "Выберите тарифный план",
        registerFailed: "Не удалось зарегистрироваться. Попробуйте ещё раз.",
        invalidCode: "Неверный или просроченный код. Попробуйте снова.",
        pricingLoadFail: "Не удалось загрузить тарифы",
    },
    ua: {
        title: "Реєстрація нового акаунта",
        at: "на My-Sites-Guard.com",
        clientArea: "Кабінет клієнта",
        orSignUpWith: "або зареєструйтесь через",
        email: "Email",
        password: "Пароль",
        show: "Показати",
        hide: "Приховати",
        next: "Далі",
        loading: "Завантаження...",
        backToLogin: "Повернутися до входу",
        step2Title: "Особисті дані",
        firstName: "Ім'я",
        lastName: "Прізвище",
        companyName: "Компанія",
        step3Title: "Тарифний план",
        choosePlan: "Оберіть тариф",
        selectPlan: "Оберіть план",
        perMonth: "/ місяць",
        step4Title: "Завершення реєстрації",
        codeSentNote: "На ваш email надіслано код підтвердження. Введіть його нижче.",
        sentTo: "Надіслано на Email:",
        type6digit: "Введіть 6-значний код",
        completeRegistration: "Завершити реєстрацію",
        accountExists: <>Акаунт з таким email вже існує. <a href="/sign-in" className="fw-bold text-danger" style={{textDecoration: "underline"}}>Увійти</a>.</>,
        requiredEmailPass: "Потрібні і email, і пароль",
        requiredAllFields: "Заповніть усі поля",
        pleaseSelectPlan: "Оберіть тарифний план",
        registerFailed: "Не вдалося зареєструватися. Спробуйте ще раз.",
        invalidCode: "Невірний або прострочений код. Спробуйте знову.",
        pricingLoadFail: "Не вдалося завантажити тарифи",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "").toLowerCase() || "en";

const LoginButton = ({ onSuccess }) => {
    const handleSuccess = (response) => {
        const token = response.credential;
        const decoded = jwtDecode(token);
        onSuccess && onSuccess({ ...decoded, token });
    };
    const handleError = () => console.log("Google login failed");
    return <GoogleLogin onSuccess={handleSuccess} onError={handleError} />;
};

const EmailMasker = ({ fullEmail, label }) => {
    const maskEmail = (email) => {
        const [local, domain] = email.split("@");
        const maskedLocal = (local || "").slice(0, 3) + "**";
        const parts = (domain || "").split(".");
        const maskedDomain = (parts[0] || "").slice(0, 4) + "**";
        const ext = parts.slice(1).join(".");
        return `${maskedLocal}@${maskedDomain}${ext ? "." + ext : ""}`;
    };
    const [maskedEmail] = useState(maskEmail(fullEmail));
    return (
        <p className="mb-2">
            {label} <strong>{maskedEmail}</strong>
        </p>
    );
};

const RegisterForm = ({ GoogleOAuthClientID, PUBLIC_URL }) => {
    const { registration, confirmRegistration } = useAuth();
    const navigate = useNavigate();

    const [lang, setLang] = useState(getInitialLang());
    const t = useMemo(() => translations[lang] || translations.en, [lang]);

    const [step, setStep] = useState(1);
    const [token, setGoogleToken] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [tariffPlan, setTariffPlan] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pricingPlans, setPricingPlans] = useState([]);
    const [values, setValues] = useState(["", "", "", "", "", ""]);
    const inputs = useRef([]);

    useEffect(() => {
        document.documentElement.lang = lang;
        localStorage.setItem("lang", lang);
    }, [lang]);

    // load pricing plans at step 3
    useEffect(() => {
        if (step === 3) {
            setLoading(true);
            axios
                .post(
                    `${AUTH_URL}api/pricing-plans`,
                    { product_id: PRODUCT_ID, lang },
                    { headers: { "Accept-Language": lang } }
                )
                .then((res) => {
                    const payload = res?.data || {};
                    const plans = Array.isArray(payload?.plans)
                        ? payload.plans
                        : Array.isArray(payload?.data)
                            ? payload.data
                            : Array.isArray(payload)
                                ? payload
                                : [];
                    setPricingPlans(plans);
                    setLoading(false);
                })
                .catch(() => {
                    setError(t.pricingLoadFail);
                    setLoading(false);
                });
        }
    }, [step, lang, t.pricingLoadFail]);

    const checkUserExists = async (emailToCheck) => {
        try {
            const res = await axios.post(
                `${AUTH_URL}api/check-user`,
                { email: emailToCheck, lang },
                { headers: { "Accept-Language": lang } }
            );
            return res.data?.exists === true;
        } catch {
            return false;
        }
    };

    const handleNextStep = () => {
        setError(null);
        setStep((s) => s + 1);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError(t.requiredEmailPass);
            return;
        }
        setLoading(true);
        const userExists = await checkUserExists(email);
        if (userExists) {
            setError(t.accountExists);
            setLoading(false);
            return;
        }
        setLoading(false);
        handleNextStep();
    };

    const handleUserDetails = (e) => {
        e.preventDefault();
        if (!firstName || !lastName || !companyName) {
            setError(t.requiredAllFields);
            return;
        }
        handleNextStep();
    };

    const handleTariffSelection = async (e) => {
        e.preventDefault();
        if (!tariffPlan) {
            setError(t.pleaseSelectPlan);
            return;
        }
        setLoading(true);
        try {
            // registration(email, password, first, last, company, tariffPlan, product_id, googleToken, lang)
            const data = await registration(
                email,
                password,
                firstName,
                lastName,
                companyName,
                tariffPlan,
                PRODUCT_ID,
                token,
                lang
            );
            if (data.status === 200) {
                if (data.user.allow_access === 0) {
                    setError(null);
                    handleNextStep(); // go to code confirm
                    return;
                }
                setError(null);
                setTimeout(() => {
                    window.location.href = data.forward;
                }, 1500);
            }
        } catch (e2) {
            setError(t.registerFailed);
        } finally {
            setLoading(false);
        }
    };

    const handleValidation = async (e) => {
        e.preventDefault();
        if (values.join("").length !== 6) {
            setError(t.invalidCode);
            return;
        }
        setLoading(true);
        try {
            // confirmRegistration(email, codeArrayOrString, lang)
            const data = await confirmRegistration(email, values, lang);
            if (data.status === 200) {
                if (data.user.allow_access === 0) {
                    setError(data.error || t.invalidCode);
                    setLoading(false);
                    return;
                }
                setError(null);
                setTimeout(() => {
                    window.location.href = data.forward;
                }, 1500);
            }
        } catch (e3) {
            setError(t.invalidCode);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (index, e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            const newValues = [...values];
            newValues[index] = value.slice(-1);
            setValues(newValues);
            if (value && index < 5) inputs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").slice(0, 6);
        if (/^\d{6}$/.test(pasteData)) {
            setValues(pasteData.split(""));
            inputs.current[5]?.focus();
        }
    };

    const selectedPlan = pricingPlans.find((p) => p.id === Number(tariffPlan));

    return (
        <div className="container mt-5">
            <h2 className="mb-1 fs-7 fw-bolder">
                {t.title}
                <br /> {t.at}
            </h2>
            <p className="mb-7">{t.clientArea}</p>
            <LanguageSwitcher lang={lang} setLang={setLang} />

            {/* STEP 1 */}
            {step === 1 && (
                <>
                    <div className="row">
                        <div className="col-12">
                            <GoogleOAuthProvider clientId={GoogleOAuthClientID}>
                                <LoginButton
                                    onSuccess={async (data) => {
                                        // prefill from Google
                                        setFirstName(data.given_name || "");
                                        setLastName(data.family_name || "");
                                        setEmail(data.email || "");
                                        setGoogleToken(data.token || "");

                                        if (!(await checkUserExists(data.email || ""))) {
                                            handleNextStep();
                                        } else {
                                            setError(t.accountExists);
                                        }
                                    }}
                                />
                            </GoogleOAuthProvider>
                        </div>
                    </div>

                    <div className="position-relative text-center my-4">
                        <p className="mb-0 fs-4 px-3 d-inline-block bg-body text-dark z-index-5 position-relative">
                            {t.orSignUpWith}
                        </p>
                        <span className="border-top w-100 position-absolute top-50 start-50 translate-middle"></span>
                    </div>

                    {error && <p className="alert alert-danger text-danger">{error}</p>}

                    <form onSubmit={handleRegister}>
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">
                                {t.email}
                            </label>
                            <input
                                type="email"
                                className="form-control"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="password" className="form-label">
                                {t.password}
                            </label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={loading}
                                >
                                    {showPassword ? t.hide : t.show}
                                </button>
                            </div>
                        </div>
                        <button type="submit" className="btn btn-primary w-100 py-8 mb-3 rounded-2" disabled={loading}>
                            {loading ? t.loading : t.next}
                        </button>
                        <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100">
                            {t.backToLogin}
                        </a>
                    </form>
                </>
            )}

            {/* STEP 2 */}
            {step === 2 && (
                <>
                    <div className="position-relative text-center my-4">
                        <p className="mb-0 fs-4 px-3 d-inline-block bg-body text-dark z-index-5 position-relative">
                            {t.step2Title}
                        </p>
                        <span className="border-top w-100 position-absolute top-50 start-50 translate-middle"></span>
                    </div>

                    {error && <p className="alert alert-danger text-danger">{error}</p>}

                    <form onSubmit={handleUserDetails}>
                        <div className="mb-3">
                            <label htmlFor="firstName" className="form-label">
                                {t.firstName}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="firstName"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="lastName" className="form-label">
                                {t.lastName}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="lastName"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="companyName" className="form-label">
                                {t.companyName}
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="companyName"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        <button type="submit" className="btn btn-primary w-100 py-8 mb-3 rounded-2" disabled={loading}>
                            {loading ? t.loading : t.next}
                        </button>
                        <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100">
                            {t.backToLogin}
                        </a>
                    </form>
                </>
            )}

            {/* STEP 3 */}
            {step === 3 && (
                <>
                    <div className="position-relative text-center my-4">
                        <p className="mb-0 fs-4 px-3 d-inline-block bg-body text-dark z-index-5 position-relative">
                            {t.step3Title}
                        </p>
                        <span className="border-top w-100 position-absolute top-50 start-50 translate-middle"></span>
                    </div>

                    {error && <p className="alert alert-danger text-danger">{error}</p>}

                    <form onSubmit={handleTariffSelection}>
                        <label className="form-label">{t.choosePlan}</label>
                        <select
                            className="form-control mb-3"
                            value={tariffPlan}
                            onChange={(e) => setTariffPlan(e.target.value)}
                            required
                            disabled={loading}
                        >
                            <option value="">{t.selectPlan}</option>
                            {pricingPlans.map((plan) => (
                                <option key={plan.id} value={plan.id}>
                                    {plan.name} - {plan.currency}{plan.price} {t.perMonth}
                                </option>
                            ))}
                        </select>

                        {selectedPlan && (
                            <div className="card p-4 mb-3 shadow-sm">
                                <h4 className="text-center fw-bold">{selectedPlan.name}</h4>
                                <p className="text-center text-primary fs-1 fw-bold">
                                    {selectedPlan.currency}
                                    {selectedPlan.price}
                                    <span className="fs-5 text-secondary"> {selectedPlan.period}</span>
                                </p>
                                {selectedPlan.description && (
                                    <p className="text-center text-body-secondary mb-3">
                                        {selectedPlan.description}
                                    </p>
                                )}
                                <ul className="list-unstyled">
                                    {selectedPlan.features?.map((feature, idx) => (
                                        <li key={idx} className="d-flex align-items-center mb-2">
                                            <span className="me-2">✔️</span>
                                            <span className="fw-semibold">{feature.name}</span>: {feature.value}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary w-100 py-2 mb-3 rounded-2" disabled={loading}>
                            {loading ? t.loading : t.next}
                        </button>
                        <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100">
                            {t.backToLogin}
                        </a>
                    </form>
                </>
            )}

            {/* STEP 4 */}
            {step === 4 && (
                <form onSubmit={handleValidation}>
                    <div className="position-relative text-center my-4">
                        <p className="mb-0 fs-4 px-3 d-inline-block bg-body text-dark z-index-5 position-relative">
                            {t.step4Title}
                        </p>
                        <span className="border-top w-100 position-absolute top-50 start-50 translate-middle"></span>
                    </div>

                    {error && <p className="alert alert-danger text-danger">{error}</p>}

                    <p>{t.codeSentNote}</p>
                    <EmailMasker fullEmail={email} label={t.sentTo} />

                    <div className="mb-3">
                        <label htmlFor="securityCode" className="form-label fw-semibold">
                            {t.type6digit}
                        </label>
                        <div className="d-flex align-items-center gap-2 gap-sm-3">
                            {values.map((value, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    className="form-control text-center"
                                    value={value}
                                    onChange={(e) => handleChange(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    maxLength="1"
                                    ref={(el) => (inputs.current[index] = el)}
                                    disabled={loading}
                                />
                            ))}
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary w-100 py-8 mb-3 rounded-2" disabled={loading}>
                        {loading ? t.loading : t.completeRegistration}
                    </button>
                    <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100">
                        {t.backToLogin}
                    </a>
                </form>
            )}
        </div>
    );
};

const Register = () => {
    const navigate = useNavigate();
    return (
        <>
            <div id="main-wrapper">
                <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                    <div className="position-relative z-index-5">
                        <div className="row">
                            <div className="col-xl-7 col-xxl-8">
                                <a href="https://my-sites-guard.com/" className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                    <img
                                        src={`https://my-sites-guard.com/assets/img/home_logo.png`}
                                        className="dark-logo"
                                        alt="Logo-Dark"
                                        style={{ maxHeight: "40px" }}
                                    />
                                </a>
                                <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                    <img src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`} alt="" className="img-fluid" width="500" />
                                </div>
                            </div>
                            <div className="col-xl-5 col-xxl-4">
                                <div className="authentication-login min-vh-100 bg-body row justify-content-center align-items-center p-4">
                                    <div className="auth-max-width col-sm-8 col-md-6 col-xl-7 px-4">
                                        <RegisterForm GoogleOAuthClientID={GoogleOAuthClientID} PUBLIC_URL={PUBLIC_URL} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="dark-transparent sidebartoggler"></div>

            {/* vendor scripts */}
            <script src="./assets/libs/bootstrap/dist/js/bootstrap.bundle.min.js"></script>
            <script src="./assets/libs/simplebar/dist/simplebar.min.js"></script>
            <script src="./assets/js/theme/app.init.js"></script>
            <script src="./assets/js/theme/theme.js"></script>
            <script src="./assets/js/theme/app.min.js"></script>
            <script src="./assets/js/theme/sidebarmenu.js"></script>
            <script src="https://cdn.jsdelivr.net/npm/iconify-icon@1.0.8/dist/iconify-icon.min.js"></script>
        </>
    );
};

export default Register;
